import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '@/api/client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Plus, Trash2, ArrowLeft, Save } from 'lucide-react';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { toast } from 'sonner';
import TESTIDS from '@/constants/testIds';

export default function BrandDetail() {
    const { id } = useParams();
    const { refresh } = useScope();
    const [brand, setBrand] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [audience, setAudience] = useState('');
    const [tones, setTones] = useState([]);
    const [offers, setOffers] = useState([]);
    const [ctas, setCtas] = useState([]);
    const [banned, setBanned] = useState([]);
    const [samples, setSamples] = useState([]);
    const [newSample, setNewSample] = useState('');
    const [newBanned, setNewBanned] = useState('');
    const [newTone, setNewTone] = useState('');
    const [newOffer, setNewOffer] = useState('');
    const [newCta, setNewCta] = useState('');
    const [colorSlot, setColorSlot] = useState(1);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const r = await apiClient.get(`/brands/${id}`);
        const b = r.data;
        setBrand(b);
        setName(b.name || '');
        setDescription(b.description || '');
        setAudience(b.audience || '');
        setTones(b.tone_keywords || []);
        setOffers(b.offers || []);
        setCtas(b.cta_preferences || []);
        setBanned(b.banned_words || []);
        setSamples(b.voice_samples || []);
        setColorSlot(b.color_slot || 1);
    };

    useEffect(() => {
        load();
    }, [id]);

    const save = async () => {
        setSaving(true);
        try {
            await apiClient.patch(`/brands/${id}`, {
                name,
                description,
                audience,
                tone_keywords: tones,
                offers,
                cta_preferences: ctas,
                banned_words: banned,
                voice_samples: samples,
                color_slot: colorSlot,
            });
            await refresh();
            toast.success('Brand saved');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    if (!brand) {
        return <div className="text-sm text-muted-foreground">Loading brand…</div>;
    }

    const accent = brandColorVar(colorSlot);

    const Chip = ({ items, setItems, color = 'neutral', testIdPrefix }) => (
        <div className="flex flex-wrap gap-1">
            {items.map((item, i) => (
                <span
                    key={`${item}-${i}`}
                    data-testid={testIdPrefix ? `${testIdPrefix}-item-${i}` : undefined}
                    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] ${
                        color === 'danger'
                            ? 'border-[hsl(var(--danger)/0.3)] bg-[hsl(var(--danger)/0.08)] text-[hsl(var(--danger))]'
                            : 'border-border bg-[hsl(var(--surface-2))] text-foreground'
                    }`}
                >
                    {item}
                    <button
                        type="button"
                        onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                        className="opacity-70 hover:opacity-100"
                        aria-label="remove"
                    >
                        <Trash2 className="h-3 w-3" />
                    </button>
                </span>
            ))}
        </div>
    );

    return (
        <div className="space-y-4" style={{ '--brand-accent-current': accent }}>
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/admin/social/brands" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground">
                        <ArrowLeft className="mr-1 h-3 w-3" /> Back to brands
                    </Link>
                    <h1 className="mt-1 text-2xl font-semibold tracking-tight">{name || 'Untitled brand'}</h1>
                    <p className="font-mono text-[12px] text-muted-foreground">/{brand.slug}</p>
                </div>
                <Button onClick={save} disabled={saving} data-testid={TESTIDS.brandSaveButton}>
                    <Save className="mr-1 h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
                </Button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card className="border-border bg-card p-4">
                    <h2 className="text-base font-semibold">Profile</h2>
                    <p className="text-xs text-muted-foreground">Core brand identity used in every generated post.</p>
                    <div className="mt-3 space-y-3">
                        <div>
                            <Label>Brand name</Label>
                            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={TESTIDS.brandEditName} />
                        </div>
                        <div>
                            <Label>Description</Label>
                            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
                        </div>
                        <div>
                            <Label>Audience</Label>
                            <Input value={audience} onChange={(e) => setAudience(e.target.value)} />
                        </div>
                        <div>
                            <Label>Brand accent color slot (1–5)</Label>
                            <div className="mt-1 flex items-center gap-2">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setColorSlot(s)}
                                        className={`flex h-7 w-7 items-center justify-center rounded-full border-2 ${
                                            colorSlot === s ? 'border-foreground' : 'border-transparent'
                                        }`}
                                        style={{ backgroundColor: `hsl(${brandColorVar(s)} / 0.6)` }}
                                        aria-label={`slot-${s}`}
                                        data-testid={`brand-color-slot-${s}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="border-border bg-card p-4">
                    <h2 className="text-base font-semibold">Voice samples</h2>
                    <p className="text-xs text-muted-foreground">Reference snippets the AI mirrors for rhythm and vocabulary.</p>
                    <div className="mt-3 space-y-2">
                        {samples.map((s, i) => (
                            <div key={i} className="flex items-start gap-2 rounded-md border border-border bg-[hsl(var(--surface-2))] p-2 text-sm">
                                <div className="flex-1">{s}</div>
                                <button
                                    type="button"
                                    onClick={() => setSamples(samples.filter((_, idx) => idx !== i))}
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label="remove sample"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <Label className="text-[11px] uppercase tracking-wider">Add sample</Label>
                                <Textarea
                                    rows={2}
                                    value={newSample}
                                    onChange={(e) => setNewSample(e.target.value)}
                                    data-testid={TESTIDS.brandAddVoiceSampleInput}
                                />
                            </div>
                            <Button
                                onClick={() => {
                                    if (!newSample.trim()) return;
                                    setSamples([...samples, newSample.trim()]);
                                    setNewSample('');
                                }}
                                size="sm"
                                data-testid={TESTIDS.brandAddVoiceSampleButton}
                            >
                                <Plus className="mr-1 h-3 w-3" /> Add
                            </Button>
                        </div>
                    </div>
                </Card>

                <Card className="border-border bg-card p-4">
                    <h2 className="text-base font-semibold">Tone & offers</h2>
                    <div className="mt-3 space-y-3">
                        <div>
                            <Label>Tone keywords</Label>
                            <Chip items={tones} setItems={setTones} />
                            <div className="mt-2 flex gap-2">
                                <Input value={newTone} onChange={(e) => setNewTone(e.target.value)} placeholder="e.g. educational" />
                                <Button size="sm" onClick={() => { if (newTone.trim()) { setTones([...tones, newTone.trim()]); setNewTone(''); } }}>Add</Button>
                            </div>
                        </div>
                        <div>
                            <Label>Offers / services</Label>
                            <Chip items={offers} setItems={setOffers} />
                            <div className="mt-2 flex gap-2">
                                <Input value={newOffer} onChange={(e) => setNewOffer(e.target.value)} placeholder="e.g. sunflower microgreens" />
                                <Button size="sm" onClick={() => { if (newOffer.trim()) { setOffers([...offers, newOffer.trim()]); setNewOffer(''); } }}>Add</Button>
                            </div>
                        </div>
                        <div>
                            <Label>Preferred CTAs</Label>
                            <Chip items={ctas} setItems={setCtas} />
                            <div className="mt-2 flex gap-2">
                                <Input value={newCta} onChange={(e) => setNewCta(e.target.value)} placeholder="e.g. DM to order" />
                                <Button size="sm" onClick={() => { if (newCta.trim()) { setCtas([...ctas, newCta.trim()]); setNewCta(''); } }}>Add</Button>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="border-border bg-card p-4">
                    <h2 className="text-base font-semibold text-[hsl(var(--danger))]">Banned words / phrases</h2>
                    <p className="text-xs text-muted-foreground">Hard guardrails. These will never appear in generated content or replies.</p>
                    <div className="mt-3 space-y-2">
                        <Chip items={banned} setItems={setBanned} color="danger" testIdPrefix="brand-banned" />
                        <div className="flex gap-2">
                            <Input
                                value={newBanned}
                                onChange={(e) => setNewBanned(e.target.value)}
                                placeholder="e.g. guaranteed, miracle cure"
                                data-testid={TESTIDS.brandAddBannedWordInput}
                            />
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    if (!newBanned.trim()) return;
                                    setBanned([...banned, newBanned.trim()]);
                                    setNewBanned('');
                                }}
                                data-testid={TESTIDS.brandAddBannedWordButton}
                            >
                                Add
                            </Button>
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
